import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Box,
  Collapse,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Checkbox,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { styled, keyframes } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); } 
`;

// Slide-in animation for timeline
const slideIn = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const FancyButton = styled(Button)`
  background: linear-gradient(45deg, #fe6b8b 30%, #ff8e53 90%);
  border: 0;
  border-radius: 3px;
  box-shadow: 0 3px 5px 2px rgba(255, 105, 135, 0.3);
  color: white;
  height: 48px;
  padding: 0 30px;
  margin-right: 8px;
  &:hover {
    animation: ${pulse} 1s infinite;
  }
`;

const RecalculateButton = styled(Button)`
  background: linear-gradient(45deg, #66bb6a 30%, #43a047 90%);
  border: 0;
  border-radius: 3px;
  box-shadow: 0 3px 5px 2px rgba(67, 160, 71, 0.3);
  color: white;
  height: 48px;
  padding: 0 30px;
  &:hover {
    animation: ${pulse} 1s infinite;
  }
`;

const FancyText = styled("span")`
  font-weight: bold;
  color: #6a0dad;
`;

const validConditions = ["Nou", "Foarte buna", "Buna", "Rezonabil"];

const ProductTable = () => {
  const [loading, setLoading] = useState({});
  const [editableValues, setEditableValues] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [productDetails, setProductDetails] = useState({});
  const [priceInRON, setPriceInRON] = useState({});
  const [priceAnalysis, setPriceAnalysis] = useState({});
  const [selectedItems, setSelectedItems] = useState({});
  const [avgPurchasePriceInRON, setAvgPurchasePriceInRON] = useState({});
  const [images, setImages] = useState({});
  const [removedMaterial, setRemovedMaterial] = useState({});
  const [consoleLogs, setConsoleLogs] = useState({});
  // NEW: expandedRows state
  const [expandedRows, setExpandedRows] = useState({});
  // NEW: resalePrices state
  const [resalePrices, setResalePrices] = useState({});
  // NEW: uploadedImages state: mapping productId to File object
  const [uploadedImages, setUploadedImages] = useState({});
  // NEW: uploadedImageUrls state: mapping productId to image URL (string)
  const [uploadedImageUrls, setUploadedImageUrls] = useState({});
  // Currency display state
  const [displayCurrency, setDisplayCurrency] = useState("RON");
  // Handler for image upload, stores File object in uploadedImages
  const handleImageUpload = (productId, file) => {
    if (!file) return;
    setUploadedImages((prev) => ({
      ...prev,
      [productId]: file,
    }));
    // If a file is uploaded, clear the image URL for this productId
    setUploadedImageUrls((prev) => ({
      ...prev,
      [productId]: "",
    }));
  };
  // Handler for image URL input
  const handleImageUrlInput = (productId, url) => {
    setUploadedImageUrls((prev) => ({
      ...prev,
      [productId]: url,
    }));
    // If a URL is set, clear the uploaded image file for this productId
    setUploadedImages((prev) => ({
      ...prev,
      [productId]: undefined,
    }));
  };
  // Helper to map Romanian conditions to English/external
  const mapConditionToExternal = (condition) => {
    if (!condition) return "";
    // Normalize for case-insensitive, trim, etc.
    const cond = condition.trim().toLowerCase();
    switch (cond) {
      case "nou":
        return "New";
      case "excelent":
        return "Excellent";
      case "foarte buna":
      case "foarte bună":
        return "Very Good";
      case "buna":
      case "bună":
        return "Good";
      case "rezonabil":
        return "Fair";
      default:
        return condition; // fallback to original
    }
  };

  const ITEMS_PER_PAGE = 50;
  const displayTotalPages = totalPages || (totalProducts > 0 ? 1 : 0);
  const displayCurrentPage =
    displayTotalPages === 0 ? 0 : Math.min(currentPage + 1, displayTotalPages);

  const stopPropagation = (event) => {
    event.stopPropagation();
  };

  const handleCalculateButtonClick = (event, productId) => {
    event.stopPropagation();
    calculatePrice(productId);
  };

  const handleRecalculateButtonClick = (event, productId) => {
    event.stopPropagation();
    recalculatePrice(productId);
  };
  // NEW: Toggle row expansion
  const toggleRowExpansion = (productId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  useEffect(() => {
    let isSubscribed = true;

    const fetchProducts = async () => {
      try {
        const response = await axios.get(
          "https://server-9p4hx.kinsta.app/api/masterdata/apianalysisstock/intrari-ai-list",
          {
            params: {
              page: currentPage + 1,
              limit: ITEMS_PER_PAGE,
              search: searchTerm || undefined,
            },
          }
        );

        if (!isSubscribed) {
          return;
        }

        const {
          items = [],
          total = 0,
          totalPages: serverTotalPages = 1,
          pageSize: serverPageSize = ITEMS_PER_PAGE,
        } = response.data || {};

        const normalizedTotal = Number(total) || 0;
        const normalizedPageSize = Math.max(
          1,
          Number(serverPageSize) || ITEMS_PER_PAGE
        );
        const normalizedServerTotalPages = Number(serverTotalPages) || 0;
        const computedTotalPages =
          normalizedTotal > 0
            ? Math.max(
                normalizedServerTotalPages,
                Math.ceil(normalizedTotal / normalizedPageSize),
                1
              )
            : 0;

        if (process.env.NODE_ENV !== "production") {
          console.log("Loaded intrari-ai page", {
            requestedPage: currentPage + 1,
            itemsReturned: items.length,
            total: normalizedTotal,
            totalPages: computedTotalPages,
            pageSize: normalizedPageSize,
          });
        }

        setTotalProducts(normalizedTotal);
        setTotalPages(computedTotalPages);
        setDisplayedProducts(items);
        setExpandedRows({});

        if (computedTotalPages > 0 && currentPage > computedTotalPages - 1) {
          setCurrentPage(Math.max(computedTotalPages - 1, 0));
        }

        setEditableValues((prevState) => {
          const nextState = { ...prevState };
          items.forEach((product) => {
            if (!nextState[product.issueId]) {
              nextState[product.issueId] = {
                summary: product.summary,
                stare: product.stare,
                pretIntrareRON: product.pretIntrareRON,
                brand: product.brand,
                model: product.model,
              };
            } else {
              if (nextState[product.issueId].brand === undefined) {
                nextState[product.issueId].brand = product.brand;
              }
              if (nextState[product.issueId].model === undefined) {
                nextState[product.issueId].model = product.model;
              }
            }
          });
          return nextState;
        });

        setLoading((prevState) => {
          const nextState = { ...prevState };
          items.forEach((product) => {
            if (nextState[product.issueId] === undefined) {
              nextState[product.issueId] = false;
            }
          });
          return nextState;
        });

        setPriceInRON((prevState) => {
          const nextState = { ...prevState };
          items.forEach((product) => {
            if (nextState[product.issueId] === undefined) {
              nextState[product.issueId] = null;
            }
          });
          return nextState;
        });

        setPriceAnalysis((prevState) => {
          const nextState = { ...prevState };
          items.forEach((product) => {
            if (nextState[product.issueId] === undefined) {
              nextState[product.issueId] = null;
            }
          });
          return nextState;
        });

        setAvgPurchasePriceInRON((prevState) => {
          const nextState = { ...prevState };
          items.forEach((product) => {
            if (nextState[product.issueId] === undefined) {
              nextState[product.issueId] = null;
            }
          });
          return nextState;
        });

        setImages((prevState) => {
          const nextState = { ...prevState };
          items.forEach((product) => {
            if (!nextState[product.issueId]) {
              nextState[product.issueId] = [];
            }
          });
          return nextState;
        });

        setRemovedMaterial((prevState) => {
          const nextState = { ...prevState };
          items.forEach((product) => {
            if (nextState[product.issueId] === undefined) {
              nextState[product.issueId] = null;
            }
          });
          return nextState;
        });

        setSelectedItems((prevState) => {
          const nextState = { ...prevState };
          items.forEach((product) => {
            if (!nextState[product.issueId]) {
              nextState[product.issueId] = {};
            }
          });
          return nextState;
        });

        setConsoleLogs((prevState) => {
          const nextState = { ...prevState };
          items.forEach((product) => {
            if (!nextState[product.issueId]) {
              nextState[product.issueId] = [];
            }
          });
          return nextState;
        });
      } catch (error) {
        if (isSubscribed) {
          console.error("Error fetching products:", error);
        }
      }
    };

    fetchProducts();

    return () => {
      isSubscribed = false;
    };
  }, [currentPage, searchTerm]);

  // Helper to convert a price to RON, using EUR or USD as base
  // Accepts either a numeric price and currency, or null/undefined
  // Synchronous version for rendering (uses cached rates if available)
  // For async fetching, see the async version in calculatePrice
  const [exchangeRates, setExchangeRates] = useState({ EUR: null, USD: null });

  // On mount, fetch EUR and USD to RON rates
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const eurResp = await axios.get(
          "https://api.exchangerate-api.com/v4/latest/EUR"
        );
        const usdResp = await axios.get(
          "https://api.exchangerate-api.com/v4/latest/USD"
        );
        setExchangeRates({
          EUR: eurResp.data.rates.RON,
          USD: usdResp.data.rates.RON,
        });
      } catch (error) {
        console.error("Error fetching exchange rates:", error);
      }
    };
    fetchRates();
  }, []);

  const convertToRON = (price, currency) => {
    if (!price || !currency) return null;
    if (currency === "EUR" && exchangeRates.EUR) {
      return (parseFloat(price) * exchangeRates.EUR).toFixed(2);
    }
    if (currency === "USD" && exchangeRates.USD) {
      return (parseFloat(price) * exchangeRates.USD).toFixed(2);
    }
    return null;
  };

  // Helper to display a price in the selected display currency
  const displayPrice = (price, currency) => {
    if (displayCurrency === "RON") {
      const ron = convertToRON(price, currency);
      return ron !== null ? `${ron} RON` : "N/A";
    }
    if (displayCurrency === currency) {
      return price !== null && price !== undefined
        ? `${price} ${currency}`
        : "N/A";
    }
    // If price is in EUR but displayCurrency is USD (or vice versa), just show original
    return price !== null && price !== undefined
      ? `${price} ${currency}`
      : "N/A";
  };

  // Helper to display stats price (used in stats box, which are always EUR from backend)
  const displayStatsPrice = (price, statCurrency = "EUR") => {
    if (displayCurrency === "RON") {
      const ron = convertToRON(price, statCurrency);
      return ron !== null ? `${ron} RON` : "N/A";
    }
    if (displayCurrency === statCurrency) {
      return price !== null && price !== undefined
        ? `${price} ${statCurrency}`
        : "N/A";
    }
    return price !== null && price !== undefined
      ? `${price} ${statCurrency}`
      : "N/A";
  };

  const handleInputChange = (productId, field, value) => {
    setEditableValues((prevState) => ({
      ...prevState,
      [productId]: {
        ...prevState[productId],
        [field]: value,
      },
    }));
  };

  const calculatePrice = async (productId) => {
    setLoading((prevState) => ({ ...prevState, [productId]: true }));
    const { summary, stare, pretIntrareRON } = editableValues[productId] || {};

    try {
      const mappedCondition = mapConditionToExternal(stare);

      if (!summary || !mappedCondition) {
        throw new Error("Summary sau conditie lipsa pentru calculul resale");
      }
      // Prepare FormData with summary and image (File object) or imageUrl
      const imageFile = uploadedImages[productId] || null;
      const imageUrl = uploadedImageUrls[productId] || "";
      const formData = new FormData();
      formData.append("summary", summary);
      if (imageFile) {
        formData.append("image", imageFile);
      } else if (imageUrl) {
        formData.append("imageUrl", imageUrl);
      }
      // Call the new /search-image endpoint with multipart/form-data
      const resaleResp = await axios.post(
        "https://server-9p4hx.kinsta.app/api/masterdata/search-image",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const resaleData = resaleResp.data || {};
      // The new response format: { results: ..., stats: ..., fallbackLinks: ..., allFilteredLinks: ..., pureResaleResultsUsed: ... }
      // We'll save results, stats, fallbackLinks, allFilteredLinks, and pureResaleResultsUsed into resalePrices[productId]
      setResalePrices((prevState) => ({
        ...prevState,
        [productId]: {
          results: resaleData.results,
          stats: resaleData.stats,
          fallbackLinks: resaleData.fallbackLinks,
          allFilteredLinks: resaleData.allFilteredLinks,
          pureResaleResultsUsed: resaleData.pureResaleResultsUsed,
        },
      }));

      // Calculate priceInRON and analysisText from stats if available
      const avgPriceUSD = Number(resaleData?.stats?.avgPrice);
      let priceInRONValue = "N/A";
      let analysisText = null;

      if (!Number.isNaN(avgPriceUSD) && avgPriceUSD > 0) {
        const convertedAvg = await convertToRON(avgPriceUSD, "USD");
        priceInRONValue = convertedAvg;

        const avgRONNumeric = parseFloat(convertedAvg);
        const pretRONNumeric = parseFloat(pretIntrareRON);

        if (
          !Number.isNaN(avgRONNumeric) &&
          avgRONNumeric > 0 &&
          !Number.isNaN(pretRONNumeric)
        ) {
          const percentageDifference = Math.abs(
            ((pretRONNumeric - avgRONNumeric) / avgRONNumeric) * 100
          ).toFixed(2);
          const comparisonText =
            pretRONNumeric > avgRONNumeric ? "mai mare" : "mai mic";
          analysisText = `Pretul nostru de intrare este de ${pretRONNumeric} RON si este ${comparisonText} decat ${avgRONNumeric.toFixed(2)} RON cu ${percentageDifference}%.`;
        }
      }

      setPriceInRON((prevState) => ({
        ...prevState,
        [productId]: priceInRONValue,
      }));
      setAvgPurchasePriceInRON((prevState) => ({
        ...prevState,
        [productId]: null,
      }));
      setPriceAnalysis((prevState) => ({
        ...prevState,
        [productId]: analysisText,
      }));
      setImages((prevState) => ({
        ...prevState,
        [productId]: [],
      }));
      setProductDetails((prevState) => ({
        ...prevState,
        [productId]: [],
      }));
      setRemovedMaterial((prevState) => ({
        ...prevState,
        [productId]: null,
      }));
      setSelectedItems((prevState) => ({
        ...prevState,
        [productId]: {},
      }));
      setConsoleLogs((prevState) => ({
        ...prevState,
        [productId]: [
          `Min resale: ${resaleData?.stats?.minPrice ?? "N/A"}`,
          `Max resale: ${resaleData?.stats?.maxPrice ?? "N/A"}`,
          `Median resale: ${resaleData?.stats?.median ?? "N/A"}`,
        ],
      }));
    } catch (error) {
      console.error("Error loading resale prices:", error);
      setConsoleLogs((prevState) => {
        const previousLogs = prevState[productId] || [];
        return {
          ...prevState,
          [productId]: [...previousLogs, `Error resale: ${error.message}`],
        };
      });
      setPriceInRON((prevState) => ({ ...prevState, [productId]: "N/A" }));
      setPriceAnalysis((prevState) => ({ ...prevState, [productId]: null }));
      setResalePrices((prevState) => ({
        ...prevState,
        [productId]: { error: error.message },
      }));
    }

    setLoading((prevState) => ({ ...prevState, [productId]: false }));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  const handleItemSelection = (productId, itemId) => {
    setSelectedItems((prevState) => ({
      ...prevState,
      [productId]: {
        ...prevState[productId],
        [itemId]: !prevState[productId][itemId],
      },
    }));
  };

  const recalculatePrice = async (productId) => {
    const selectedProductDetails = productDetails[productId];
    const selections = selectedItems[productId];

    if (!selectedProductDetails || selectedProductDetails.length === 0) {
      return;
    }

    if (!selections) {
      return;
    }

    const selectedProductIds = Object.keys(selections).filter(
      (itemId) => selections[itemId]
    );
    const selectedProducts = selectedProductDetails.filter((product) =>
      selectedProductIds.includes(product.id)
    );

    const avgSalePrice =
      selectedProducts.reduce(
        (acc, product) =>
          acc + (parseFloat(product.Price_sale) || parseFloat(product.Price)),
        0
      ) / selectedProducts.length;

    const avgPurchasePrice =
      selectedProducts.reduce(
        (acc, product) => acc + parseFloat(product.Purchase_price),
        0
      ) / selectedProducts.length;

    const avgPriceInRON = await convertToRON(avgSalePrice, "USD");
    const avgPurchasePriceInRON = await convertToRON(avgPurchasePrice, "USD");

    setPriceInRON((prevState) => ({
      ...prevState,
      [productId]: avgPriceInRON,
    }));
    setAvgPurchasePriceInRON((prevState) => ({
      ...prevState,
      [productId]: avgPurchasePriceInRON,
    }));

    const pretIntrareRON = editableValues[productId].pretIntrareRON;
    const percentageDifference = Math.abs(
      ((pretIntrareRON - avgPurchasePriceInRON) / avgPurchasePriceInRON) * 100
    ).toFixed(2);
    const comparisonText =
      pretIntrareRON > avgPurchasePriceInRON ? "mai mare" : "mai mic";

    const analysisText = `Pretul nostru de intrare este de ${pretIntrareRON} RON si este ${comparisonText} decat ${avgPurchasePriceInRON} RON cu ${percentageDifference}%.`;

    setPriceAnalysis((prevState) => ({
      ...prevState,
      [productId]: analysisText,
    }));
  };

  return (
    <TableContainer>
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <TextField
          placeholder="Search..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          fullWidth
          margin="normal"
          sx={{ flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="display-currency-label">Monedă afișare</InputLabel>
          <Select
            labelId="display-currency-label"
            value={displayCurrency}
            label="Monedă afișare"
            onChange={(e) => setDisplayCurrency(e.target.value)}
          >
            <MenuItem value="RON">RON</MenuItem>
            <MenuItem value="EUR">EUR</MenuItem>
            <MenuItem value="USD">USD</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Summary</TableCell>
            <TableCell>Conditie</TableCell>
            <TableCell>Pret Intrare</TableCell>
            <TableCell>Pret de Vânzare (calculat)</TableCell>
            <TableCell>Predicție Vânzare</TableCell>
            <TableCell>Plan Reducere</TableCell>
            <TableCell>Data cerere</TableCell>
            <TableCell>Status</TableCell>
            <TableCell style={{ minWidth: "200px" }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayedProducts.map((product) => (
            <React.Fragment key={product.issueId}>
              <TableRow
                key={product.issueId}
                onClick={() => toggleRowExpansion(product.issueId)}
                style={{ cursor: "pointer" }}
              >
                <TableCell>{product.issueId}</TableCell>
                <TableCell>
                  <TextField
                    value={editableValues[product.issueId]?.summary || ""}
                    onChange={(e) =>
                      handleInputChange(
                        product.issueId,
                        "summary",
                        e.target.value
                      )
                    }
                    onClick={stopPropagation}
                    fullWidth
                    multiline
                    rows={3}
                    key={`summary-${product.issueId}`}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={editableValues[product.issueId]?.stare || ""}
                    onChange={(e) =>
                      handleInputChange(
                        product.issueId,
                        "stare",
                        e.target.value
                      )
                    }
                    onClick={stopPropagation}
                    fullWidth
                    key={`stare-${product.issueId}`}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={
                      editableValues[product.issueId]?.pretIntrareRON || ""
                    }
                    onChange={(e) =>
                      handleInputChange(
                        product.issueId,
                        "pretIntrareRON",
                        e.target.value
                      )
                    }
                    onClick={stopPropagation}
                    fullWidth
                    key={`pretIntrareRON-${product.issueId}`}
                  />
                </TableCell>
                <TableCell>
                  {loading[product.issueId] ? (
                    <CircularProgress />
                  ) : (
                    // priceInRON is in RON, but we want to show in selected currency
                    (() => {
                      // If priceInRON is set, it's a string in RON or "N/A"
                      const priceRON = priceInRON[product.issueId];
                      if (!priceRON || priceRON === "N/A") return "N/A";
                      if (displayCurrency === "RON") {
                        return `${priceRON} RON`;
                      } else {
                        // Convert RON to EUR/USD for display (approximate using exchangeRates)
                        const ronValue = parseFloat(priceRON);
                        if (displayCurrency === "EUR" && exchangeRates.EUR) {
                          return `${(ronValue / exchangeRates.EUR).toFixed(2)} EUR`;
                        }
                        if (displayCurrency === "USD" && exchangeRates.USD) {
                          return `${(ronValue / exchangeRates.USD).toFixed(2)} USD`;
                        }
                        return `${priceRON} RON`;
                      }
                    })()
                  )}
                </TableCell>
                <TableCell>
                  {/* Mock predicție vânzare */}
                  45 zile (🟡)
                </TableCell>
                <TableCell>
                  {/* Mock plan reducere */}
                  0–30z: 0%, 31–60z: -5%, 61–90z: -10%
                </TableCell>
                <TableCell>
                  {new Date(product.createdDate).toLocaleDateString()}
                </TableCell>
                <TableCell>{product.status}</TableCell>
                <TableCell>
                  <Box display="flex">
                    <FancyButton
                      onClick={(event) =>
                        handleCalculateButtonClick(event, product.issueId)
                      }
                      disabled={
                        !validConditions.includes(
                          editableValues[product.issueId]?.stare
                        )
                      }
                    >
                      Calculate Price
                    </FancyButton>
                    {selectedItems[product.issueId] &&
                      Object.values(selectedItems[product.issueId]).some(
                        (selected) => selected
                      ) && (
                        <RecalculateButton
                          onClick={(event) =>
                            handleRecalculateButtonClick(event, product.issueId)
                          }
                        >
                          Recalculate Price
                        </RecalculateButton>
                      )}
                  </Box>
                </TableCell>
              </TableRow>
              {consoleLogs[product.issueId] &&
                consoleLogs[product.issueId].length > 0 && (
                  <TableRow key={`logs-${product.issueId}`}>
                    <TableCell colSpan={8}>
                      <Collapse in={!!consoleLogs[product.issueId].length}>
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                        >
                          <Box
                            textAlign="center"
                            mb={2}
                            p={2}
                            style={{
                              backgroundColor: "#f5f5f5",
                              borderRadius: "8px",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            }}
                          >
                            <Typography
                              variant="body2"
                              style={{ color: "#333", fontFamily: "monospace" }}
                            >
                              {consoleLogs[product.issueId].map(
                                (log, index) => (
                                  <div key={index}>{log}</div>
                                )
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              {expandedRows[product.issueId] && (
                <TableRow key={`details-${product.issueId}`}>
                  <TableCell colSpan={10}>
                    <Collapse in={expandedRows[product.issueId]}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <Box textAlign="center" mb={2}>
                          <FancyText>
                            {priceAnalysis[product.issueId]}
                          </FancyText>
                        </Box>
                        {/* Image upload and URL input under resale analysis */}
                        <Box textAlign="center" mb={2}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Încarcă o imagine sau introdu URL pentru analiză
                            resale:
                          </Typography>
                          <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            justifyContent="center"
                            mt={1}
                            gap={2}
                          >
                            <input
                              accept="image/*"
                              type="file"
                              style={{ marginTop: 8 }}
                              onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                  handleImageUpload(
                                    product.issueId,
                                    e.target.files[0]
                                  );
                                }
                              }}
                              value={undefined}
                            />
                            <TextField
                              label="sau URL imagine"
                              size="small"
                              value={uploadedImageUrls[product.issueId] || ""}
                              onChange={(e) =>
                                handleImageUrlInput(
                                  product.issueId,
                                  e.target.value
                                )
                              }
                              style={{ minWidth: 220 }}
                            />
                          </Box>
                          {(uploadedImages[product.issueId] ||
                            uploadedImageUrls[product.issueId]) && (
                            <Box
                              mt={1}
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Imagine selectată:
                              </Typography>
                              <img
                                src={
                                  uploadedImages[product.issueId]
                                    ? URL.createObjectURL(
                                        uploadedImages[product.issueId]
                                      )
                                    : uploadedImageUrls[product.issueId]
                                }
                                alt="Uploaded"
                                style={{
                                  maxWidth: "180px",
                                  maxHeight: "180px",
                                  marginTop: 6,
                                  borderRadius: 6,
                                  border: "1px solid #eee",
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                        {/* Analiza resale extern reală și listări rezultate */}
                        <Box textAlign="center" mb={2}>
                          {resalePrices[product.issueId] ? (
                            resalePrices[product.issueId].error ? (
                              <Typography variant="body2" color="error">
                                Eroare resale:{" "}
                                {resalePrices[product.issueId].error}
                              </Typography>
                            ) : (
                              <>
                                {/* Listings grid */}
                                {Array.isArray(
                                  resalePrices[product.issueId].results
                                ) &&
                                resalePrices[product.issueId].results.length >
                                  0 ? (
                                  <Box mt={2}>
                                    <Typography
                                      variant="subtitle2"
                                      gutterBottom
                                    >
                                      Resale results
                                    </Typography>
                                    <Grid
                                      container
                                      spacing={2}
                                      justifyContent="center"
                                    >
                                      {resalePrices[
                                        product.issueId
                                      ].results.map((result, idx) => {
                                        // Extract aiResult and priceValue/currency for rendering
                                        const ai = result.aiResult || {};
                                        const priceValue = result.priceValue;
                                        const currency = ai.currency;
                                        // Extract hostname from result.link
                                        let hostname = "";
                                        if (result.link) {
                                          try {
                                            hostname = new URL(result.link)
                                              .hostname;
                                          } catch (e) {
                                            hostname = "";
                                          }
                                        }
                                        return (
                                          <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            md={4}
                                            key={idx}
                                          >
                                            <Box
                                              border={1}
                                              borderColor="grey.300"
                                              borderRadius={1}
                                              p={2}
                                              textAlign="center"
                                              sx={{
                                                backgroundColor: "#fafaff",
                                                position: "relative",
                                              }}
                                            >
                                              {/* Card header-like element for hostname */}
                                              {hostname && (
                                                <Box
                                                  sx={{
                                                    backgroundColor: "#d1d5db",
                                                    borderTopLeftRadius: 7,
                                                    borderTopRightRadius: 7,
                                                    fontWeight: 700,
                                                    px: 1.5,
                                                    py: 0.9,
                                                    mb: 1.2,
                                                    width: "calc(100% + 32px)",
                                                    position: "absolute",
                                                    top: 0,
                                                    left: -16,
                                                    textAlign: "center",
                                                    fontSize: "1.05em",
                                                    letterSpacing: "0.03em",
                                                    zIndex: 2,
                                                  }}
                                                >
                                                  <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                      fontWeight: 700,
                                                      color: "#222",
                                                      letterSpacing: "0.02em",
                                                    }}
                                                  >
                                                    {hostname}
                                                  </Typography>
                                                </Box>
                                              )}
                                              <Box
                                                sx={{ mt: hostname ? 4 : 0 }}
                                              >
                                                {ai.image && (
                                                  <img
                                                    src={ai.image}
                                                    alt={ai.title || "Listing"}
                                                    style={{
                                                      width: "100%",
                                                      height: 140,
                                                      objectFit: "cover",
                                                      borderRadius: 8,
                                                      marginBottom: 8,
                                                      marginTop: 0,
                                                    }}
                                                  />
                                                )}
                                                <Typography
                                                  variant="body2"
                                                  noWrap
                                                  title={ai.title || "Listing"}
                                                >
                                                  {ai.title || "Listing"}
                                                </Typography>
                                                <Typography
                                                  variant="subtitle2"
                                                  color="primary"
                                                  sx={{ mt: 0.5 }}
                                                >
                                                  {priceValue && currency
                                                    ? displayPrice(
                                                        priceValue,
                                                        currency
                                                      )
                                                    : ai.price
                                                      ? ai.price
                                                      : "N/A"}
                                                </Typography>
                                                {result.link && (
                                                  <Typography
                                                    variant="caption"
                                                    display="block"
                                                    sx={{ mt: 0.5 }}
                                                  >
                                                    <a
                                                      href={result.link}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      style={{
                                                        textDecoration:
                                                          "underline",
                                                      }}
                                                    >
                                                      Vezi pe site
                                                    </a>
                                                  </Typography>
                                                )}
                                              </Box>
                                            </Box>
                                          </Grid>
                                        );
                                      })}
                                    </Grid>
                                  </Box>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Nu există rezultate resale.
                                  </Typography>
                                )}
                                {/* Stats below grid */}
                                {resalePrices[product.issueId].stats && (
                                  <Box
                                    display="inline-block"
                                    p={2}
                                    mt={2}
                                    sx={{
                                      backgroundColor: "#f2f2fa",
                                      borderRadius: "10px",
                                      boxShadow:
                                        "0 1px 4px rgba(80,0,120,0.07)",
                                      minWidth: "220px",
                                    }}
                                  >
                                    {/* Min resale */}
                                    <Typography variant="body2">
                                      <b>Min resale extern:</b>{" "}
                                      {resalePrices[product.issueId]?.stats
                                        ?.minPrice === "N/A" ? (
                                        <Typography
                                          variant="body2"
                                          component="span"
                                          sx={{
                                            color: "grey.600",
                                            fontStyle: "italic",
                                          }}
                                        >
                                          N/A
                                        </Typography>
                                      ) : (
                                        displayStatsPrice(
                                          resalePrices[product.issueId].stats
                                            .minPrice,
                                          "EUR"
                                        )
                                      )}
                                    </Typography>
                                    {/* Max resale */}
                                    <Typography variant="body2">
                                      <b>Max resale extern:</b>{" "}
                                      {resalePrices[product.issueId]?.stats
                                        ?.maxPrice === "N/A" ? (
                                        <Typography
                                          variant="body2"
                                          component="span"
                                          sx={{
                                            color: "grey.600",
                                            fontStyle: "italic",
                                          }}
                                        >
                                          N/A
                                        </Typography>
                                      ) : (
                                        displayStatsPrice(
                                          resalePrices[product.issueId].stats
                                            .maxPrice,
                                          "EUR"
                                        )
                                      )}
                                    </Typography>
                                    {/* Avg resale */}
                                    <Typography variant="body2">
                                      <b>Avg resale extern:</b>{" "}
                                      {resalePrices[product.issueId]?.stats
                                        ?.avgPrice === "N/A" ? (
                                        <Typography
                                          variant="body2"
                                          component="span"
                                          sx={{
                                            color: "grey.600",
                                            fontStyle: "italic",
                                          }}
                                        >
                                          N/A
                                        </Typography>
                                      ) : (
                                        displayStatsPrice(
                                          resalePrices[product.issueId].stats
                                            .avgPrice,
                                          "EUR"
                                        )
                                      )}
                                    </Typography>
                                    {/* Median resale */}
                                    <Typography variant="body2">
                                      <b>Median resale extern:</b>{" "}
                                      {resalePrices[product.issueId]?.stats
                                        ?.median === "N/A" ? (
                                        <Typography
                                          variant="body2"
                                          component="span"
                                          sx={{
                                            color: "grey.600",
                                            fontStyle: "italic",
                                          }}
                                        >
                                          N/A
                                        </Typography>
                                      ) : (
                                        displayStatsPrice(
                                          resalePrices[product.issueId].stats
                                            .median,
                                          "EUR"
                                        )
                                      )}
                                    </Typography>
                                  </Box>
                                )}
                                {/* Fallback links section */}
                                {Array.isArray(
                                  resalePrices[product.issueId].fallbackLinks
                                ) &&
                                  resalePrices[product.issueId].fallbackLinks
                                    .length > 0 && (
                                    <Box mt={3}>
                                      <Typography
                                        variant="subtitle2"
                                        gutterBottom
                                      >
                                        Fallback links
                                      </Typography>
                                      <ul
                                        style={{
                                          paddingLeft: 20,
                                          textAlign: "left",
                                        }}
                                      >
                                        {resalePrices[
                                          product.issueId
                                        ].fallbackLinks.map((item, idx) => {
                                          // Extract hostname from item.link
                                          let hostname = "";
                                          try {
                                            hostname = new URL(item.link)
                                              .hostname;
                                          } catch (e) {
                                            hostname = "";
                                          }
                                          return (
                                            <li
                                              key={idx}
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 8,
                                              }}
                                            >
                                              <a
                                                href={item.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                  textDecoration: "underline",
                                                }}
                                              >
                                                {item.title || item.link}
                                              </a>
                                              {hostname && (
                                                <Typography
                                                  variant="caption"
                                                  component="span"
                                                  sx={{
                                                    backgroundColor: "#e0e0e0",
                                                    borderRadius: "6px",
                                                    px: 1,
                                                    py: 0.2,
                                                    ml: 0.5,
                                                    fontWeight: 500,
                                                  }}
                                                >
                                                  {hostname}
                                                </Typography>
                                              )}
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </Box>
                                  )}
                                {/* All filtered links section */}
                                {Array.isArray(
                                  resalePrices[product.issueId].allFilteredLinks
                                ) &&
                                  resalePrices[product.issueId].allFilteredLinks
                                    .length > 0 && (
                                    <Box mt={3}>
                                      <Typography
                                        variant="subtitle2"
                                        gutterBottom
                                      >
                                        All filtered links
                                      </Typography>
                                      <ul
                                        style={{
                                          paddingLeft: 20,
                                          textAlign: "left",
                                        }}
                                      >
                                        {resalePrices[
                                          product.issueId
                                        ].allFilteredLinks.map((item, idx) => {
                                          let hostname = "";
                                          try {
                                            hostname = new URL(item.link)
                                              .hostname;
                                          } catch (e) {
                                            hostname = "";
                                          }
                                          return (
                                            <li
                                              key={idx}
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 8,
                                              }}
                                            >
                                              <a
                                                href={item.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                  textDecoration: "underline",
                                                }}
                                              >
                                                {item.title || item.link}
                                              </a>
                                              {hostname && (
                                                <Typography
                                                  variant="caption"
                                                  component="span"
                                                  sx={{
                                                    backgroundColor: "#e0e0e0",
                                                    borderRadius: "6px",
                                                    px: 1,
                                                    py: 0.2,
                                                    ml: 0.5,
                                                    fontWeight: 500,
                                                  }}
                                                >
                                                  {hostname}
                                                </Typography>
                                              )}
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </Box>
                                  )}
                                {/* Pure resale results used for stats */}
                                {Array.isArray(
                                  resalePrices[product.issueId]
                                    .pureResaleResultsUsed
                                ) &&
                                  resalePrices[product.issueId]
                                    .pureResaleResultsUsed.length > 0 && (
                                    <Box mt={3}>
                                      <Typography
                                        variant="subtitle2"
                                        gutterBottom
                                      >
                                        Pure resale results used for stats
                                      </Typography>
                                      <ul
                                        style={{
                                          paddingLeft: 20,
                                          textAlign: "left",
                                        }}
                                      >
                                        {resalePrices[
                                          product.issueId
                                        ].pureResaleResultsUsed.map(
                                          (item, idx) => {
                                            // Use aiResult and priceValue/currency for rendering
                                            const ai = item.aiResult || {};
                                            const priceValue = item.priceValue;
                                            const currency = ai.currency;
                                            return (
                                              <li
                                                key={idx}
                                                style={{ marginBottom: 6 }}
                                              >
                                                <span
                                                  style={{ fontWeight: 500 }}
                                                >
                                                  {ai.title ||
                                                    item.title ||
                                                    "Listing"}
                                                </span>
                                                {" – "}
                                                <span>
                                                  {priceValue && currency
                                                    ? displayPrice(
                                                        priceValue,
                                                        currency
                                                      )
                                                    : ai.price
                                                      ? ai.price
                                                      : item.price
                                                        ? item.price
                                                        : "N/A"}
                                                </span>
                                                {" – "}
                                                {item.link && (
                                                  <a
                                                    href={item.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                      textDecoration:
                                                        "underline",
                                                    }}
                                                  >
                                                    Link
                                                  </a>
                                                )}
                                              </li>
                                            );
                                          }
                                        )}
                                      </ul>
                                    </Box>
                                  )}
                              </>
                            )
                          ) : (
                            <Typography variant="body2">N/A</Typography>
                          )}
                        </Box>
                        {/* Planul reducerilor mock */}
                        <Box textAlign="center" mb={2}>
                          <Typography variant="body2">
                            Reduceri planificate: Ziua 0–30: 0%, Ziua 31–60:
                            -5%, Ziua 61–90: -10%
                          </Typography>
                        </Box>
                        {/* Timeline reduceri (mock) */}
                        <Box
                          width="100%"
                          mt={2}
                          mb={2}
                          sx={{
                            animation: `${slideIn} 0.6s ease-in-out`,
                          }}
                        >
                          <Typography variant="subtitle2" align="center">
                            Timeline reduceri (mock)
                          </Typography>
                          <Box
                            display="flex"
                            flexDirection="row"
                            width="80%"
                            mx="auto"
                            mt={1}
                          >
                            <Box
                              flex={1}
                              bgcolor="#4caf50"
                              p={1}
                              textAlign="center"
                            >
                              0-30z: 0%
                            </Box>
                            <Box
                              flex={1}
                              bgcolor="#ff9800"
                              p={1}
                              textAlign="center"
                            >
                              31-60z: -5%
                            </Box>
                            <Box
                              flex={1}
                              bgcolor="#f44336"
                              p={1}
                              textAlign="center"
                            >
                              61-90z: -10%
                            </Box>
                          </Box>
                        </Box>
                        {images[product.issueId] &&
                          images[product.issueId].length > 0 && (
                            <Grid container spacing={2}>
                              {images[product.issueId].map((image, index) => (
                                <Grid item xs={12} sm={6} md={3} key={index}>
                                  <Box
                                    display="flex"
                                    flexDirection="column"
                                    justifyContent="center"
                                    alignItems="center"
                                    width="100%"
                                    my={2}
                                    style={{ pageBreakInside: "avoid" }}
                                  >
                                    <Checkbox
                                      checked={
                                        selectedItems[product.issueId] &&
                                        selectedItems[product.issueId][
                                          productDetails[product.issueId][index]
                                            .id
                                        ]
                                      }
                                      onChange={() =>
                                        handleItemSelection(
                                          product.issueId,
                                          productDetails[product.issueId][index]
                                            .id
                                        )
                                      }
                                    />
                                    <img
                                      src={image}
                                      alt="Product"
                                      style={{ width: "200px", margin: "10px" }}
                                    />
                                    {productDetails[product.issueId][index] && (
                                      <>
                                        <Box textAlign="center" mt={1}>
                                          <Typography
                                            noWrap
                                            variant="body2"
                                            style={{
                                              maxWidth: "200px",
                                              wordWrap: "break-word",
                                            }}
                                          >
                                            ID:{" "}
                                            {
                                              productDetails[product.issueId][
                                                index
                                              ].id
                                            }
                                          </Typography>
                                        </Box>
                                        <Box textAlign="center" mt={1}>
                                          <Typography
                                            noWrap
                                            variant="body2"
                                            style={{
                                              maxWidth: "200px",
                                              wordWrap: "break-word",
                                            }}
                                          >
                                            {productDetails[product.issueId][
                                              index
                                            ].Model === "N/A"
                                              ? productDetails[product.issueId][
                                                  index
                                                ].Namemodel
                                              : productDetails[product.issueId][
                                                  index
                                                ].Model}
                                          </Typography>
                                        </Box>
                                        <Box textAlign="center" mt={1}>
                                          <FancyText>
                                            Conditie:{" "}
                                            {
                                              productDetails[product.issueId][
                                                index
                                              ].Conditio
                                            }
                                          </FancyText>
                                        </Box>
                                        <Box textAlign="center" mt={1}>
                                          <FancyText>
                                            Insotit de:{" "}
                                            {
                                              productDetails[product.issueId][
                                                index
                                              ].ComesWith
                                            }
                                          </FancyText>
                                        </Box>
                                      </>
                                    )}
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          )}
                        {removedMaterial[product.issueId] && (
                          <Box
                            textAlign="center"
                            style={{
                              textDecoration: "line-through",
                              fontWeight: "bold",
                            }}
                          >
                            Material negasit:{" "}
                            {removedMaterial[product.issueId].toUpperCase()}
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
      <Box display="flex" justifyContent="center" mt={2}>
        <Button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
          disabled={displayTotalPages <= 1 || currentPage <= 0}
        >
          Previous
        </Button>
        <Button
          onClick={() =>
            setCurrentPage((prev) =>
              prev + 1 >= displayTotalPages ? prev : prev + 1
            )
          }
          disabled={
            displayTotalPages <= 1 || currentPage >= displayTotalPages - 1
          }
        >
          Next
        </Button>
        <Typography variant="body2" sx={{ ml: 2 }} alignSelf="center">
          Page {displayCurrentPage} of {displayTotalPages} | Items{" "}
          {totalProducts}
        </Typography>
      </Box>
    </TableContainer>
  );
};

export default ProductTable;
